import { TID } from "@/constants/testIds";
import { ArrowRight, Fingerprint, Sparkles } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const ALTARA_LOGO = "https://customer-assets.emergentagent.com/job_413a4d1d-7808-4808-afdb-26c6e8aa4d99/artifacts/mgbxngl3_Altara%20New.png";

export default function LoginPage() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [previewBusy, setPreviewBusy] = useState(false);

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const beginGoogle = () => {
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const signInAsPreviewAdmin = async () => {
    setPreviewBusy(true);
    try {
      const { data } = await api.post("/auth/preview-admin");
      setUser(data);
      navigate("/dashboard", { replace: true, state: { user: data } });
    } catch (e) {
      console.error("preview admin sign-in failed", e);
      setPreviewBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      {/* Left: brand pane */}
      <div className="relative hidden lg:flex flex-col justify-between p-10 altara-noise bg-gradient-to-br from-altara-ink via-altara-deep to-[#0e3a5f] text-white">
        <div className="absolute inset-0 opacity-20 altara-grid-pattern pointer-events-none" />
        <div className="relative">
          <img src={ALTARA_LOGO} alt="Altara" className="h-20 md:h-24 w-auto brightness-0 invert" />
          <div className="mt-5 text-xs md:text-sm uppercase tracking-[0.32em] text-altara-teal-bright font-semibold">
            Consumer Intelligence
          </div>
        </div>
        <div className="relative space-y-6">
          <div className="text-[10px] uppercase tracking-[0.22em] text-altara-teal-bright/80">Our promise</div>
          <h2 className="font-display text-3xl leading-tight max-w-md">
            Every digital decision creates a trust obligation.
            <span className="block mt-2 text-altara-teal-bright/90">We make trust measurable.</span>
          </h2>
          <div className="flex items-center gap-3 pt-4 border-t border-white/10">
            <Fingerprint className="w-5 h-5 text-altara-teal-bright" />
            <div className="text-xs text-white/70">
              Multi-tenant · Event-driven · Evidence-first · Explainable by design.
            </div>
          </div>
        </div>
        <div className="relative text-[10px] text-white/40 font-mono">
          IOSCO TechSprint · 2026 · FSCA-mentored
        </div>
      </div>

      {/* Right: auth pane */}
      <div className="flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-sm">
          <Link to="/" className="inline-flex items-center gap-2 text-xs text-muted-foreground hover:text-altara-deep mb-8">
            ← Back to overview
          </Link>
          <h1 className="font-display text-3xl font-semibold text-altara-ink tracking-tight">Sign in</h1>
          <p className="mt-2 text-sm text-slate-600">
            Access the Altara Consumer Intelligence console.
          </p>

          <button
            data-testid={TID.loginGoogleBtn}
            onClick={beginGoogle}
            className="mt-8 w-full flex items-center justify-center gap-3 bg-altara-deep hover:bg-altara-ink text-white text-sm px-4 py-3 rounded-sm transition-colors"
          >
            <GoogleGlyph />
            Continue with Google
            <ArrowRight className="w-4 h-4" />
          </button>

          <div className="mt-8">
            <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground mb-3">
              Enterprise SSO · placeholder adapters
            </div>
            <div className="grid grid-cols-2 gap-2">
              {[
                { label: "Microsoft Entra ID", tid: TID.loginSsoEntra },
                { label: "Okta", tid: TID.loginSsoOkta },
                { label: "Auth0", tid: TID.loginSsoAuth0 },
                { label: "Keycloak", tid: TID.loginSsoKeycloak },
              ].map(({ label, tid }) => (
                <button
                  key={label}
                  data-testid={tid}
                  disabled
                  title="Placeholder — SSO adapter will be wired when credentials are provided."
                  className="text-left px-3 py-2.5 border border-dashed border-slate-300 rounded-sm text-xs text-slate-500 hover:border-slate-400 cursor-not-allowed opacity-90 bg-white"
                >
                  <div className="font-medium text-slate-700">{label}</div>
                  <div className="text-[10px] uppercase tracking-[0.15em] text-teal-700">Stubbed</div>
                </button>
              ))}
            </div>
          </div>

          <p className="mt-8 text-[11px] text-muted-foreground leading-relaxed">
            By continuing, you accept the Altara Responsible AI policy. Enterprise SSO wiring becomes available once your
            tenant administrator provides Entra / Okta / Auth0 / Keycloak client credentials.
          </p>

          {/* Preview access — visible in review / demo / testing environments */}
          <div className="mt-8 pt-6 border-t border-dashed border-altara-teal/40">
            <div className="text-[10px] uppercase tracking-[0.22em] text-altara-teal font-semibold mb-3">
              Preview access · review, test &amp; refine
            </div>
            <button
              data-testid="login-preview-admin-btn"
              onClick={signInAsPreviewAdmin}
              disabled={previewBusy}
              className="w-full flex items-center justify-between gap-3 border border-altara-teal bg-teal-50/30 hover:bg-altara-teal hover:text-white text-altara-deep text-sm px-4 py-3 rounded-sm transition-colors group disabled:opacity-70 disabled:cursor-progress"
            >
              <span className="flex items-center gap-2.5">
                <span className="w-7 h-7 rounded-sm bg-altara-teal text-white flex items-center justify-center group-hover:bg-white group-hover:text-altara-teal transition-colors">
                  <Sparkles className="w-3.5 h-3.5" />
                </span>
                <span className="font-medium">
                  {previewBusy ? "Signing in…" : "Altara Admin Preview"}
                </span>
              </span>
              <ArrowRight className="w-4 h-4" />
            </button>
            <p className="mt-2 text-[10px] text-slate-500 leading-relaxed">
              One-click preview access. Grants full administrator privileges on the Altara Preview environment for
              review, testing and refinement. Remove or gate in production.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function GoogleGlyph() {
  return (
    <svg width="16" height="16" viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
    </svg>
  );
}
